import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { filmAPI } from '../../services/api';
import './FilmEdit.css';

const BACKEND_URL = 'https://impactstoriesfund.org/backend/public/';

const FilmEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtube_link: '',
    cover_image: null,
  });
  const [preview, setPreview] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [originalData, setOriginalData] = useState(null); // Track original data

  useEffect(() => {
    fetchFilmData();
  }, [id]);

  const fetchFilmData = async () => {
    try {
      setFetching(true);
      const response = await filmAPI.getFilm(id);
      const film = response.data;
      
      // Save original data for comparison
      setOriginalData({
        title: film.title || '',
        description: film.description || '',
        youtube_link: film.youtube_link || '',
      });
      
      setFormData({
        title: film.title || '',
        description: film.description || '',
        youtube_link: film.youtube_link || '',
        cover_image: null,
      });
      
      if (film.image_path) {
        setCurrentImage(BACKEND_URL + film.image_path);
        setPreview(BACKEND_URL + film.image_path);
      }
      
      setFetching(false);
    } catch (error) {
      console.error('Error fetching film:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to load film data. Please try again.' 
      });
      setFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size must be less than 2MB' });
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        cover_image: file,
      }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Create FormData object for multipart/form-data
      const submitData = new FormData();
      
      // Only include fields that have changed
      if (formData.title !== originalData.title) {
        submitData.append('title', formData.title);
      }
      
      if (formData.description !== originalData.description) {
        submitData.append('description', formData.description);
      }
      
      if (formData.youtube_link !== originalData.youtube_link) {
        submitData.append('youtube_link', formData.youtube_link);
      }
      
      // Only include image if a new one was selected
      if (formData.cover_image) {
        submitData.append('cover_image', formData.cover_image);
      }
      
      // If nothing changed, show message and return
      if (submitData.entries().next().done && !formData.cover_image) {
        setMessage({ type: 'info', text: 'No changes detected.' });
        setLoading(false);
        return;
      }

      // Append the _method for Laravel if needed (PUT/PATCH)
      submitData.append('_method', 'PUT');

      // Send the FormData object
      await filmAPI.updateFilm(id, submitData);
      
      setMessage({ 
        type: 'success', 
        text: 'Film updated successfully! Redirecting...' 
      });
      
      setTimeout(() => {
        navigate('/films');
      }, 2000);
    } catch (error) {
      console.error('Update error:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 
              error.response?.data?.message || 
              error.response?.data?.errors?.[0] || 
              'Failed to update film' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      cover_image: null,
    }));
    setPreview(currentImage);
    document.getElementById('cover_image').value = '';
  };

  if (fetching) {
    return (
      <AdminLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading film data...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="film-edit-container">
        <div className="edit-header">
          <h1>Edit Film</h1>
          <p>Update film information</p>
        </div>
        
        <form onSubmit={handleSubmit} className="edit-form">
          <div className="form-row">
            <div className="form-column">
              <div className="form-group">
                <label>Film Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter film title"
                />
              </div>
              
              <div className="form-group">
                <label>YouTube Link *</label>
                <input
                  type="url"
                  name="youtube_link"
                  value={formData.youtube_link}
                  onChange={handleInputChange}
                  required
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <small className="form-hint">
                  Full YouTube URL including https://
                </small>
              </div>
              
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows="6"
                  placeholder="Describe the film..."
                  maxLength="500"
                />
                <small className="form-hint">
                  {formData.description.length}/500 characters
                </small>
              </div>
            </div>
            
            <div className="form-column">
              <div className="form-group">
                <label>Cover Image</label>
                <div className="image-upload-section">
                  <div className="file-upload-area">
                    <input
                      id="cover_image"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="file-input"
                    />
                    <label htmlFor="cover_image" className="file-label">
                      {preview && preview !== currentImage ? 'Change Image' : 'Upload New Image'}
                    </label>
                    <small className="form-hint">
                      Max size: 2MB • Formats: JPG, PNG, GIF
                    </small>
                    <small className="form-hint">
                      Leave empty to keep current image
                    </small>
                  </div>
                  
                  {preview && (
                    <div className="image-preview-section">
                      <div className="current-image-info">
                        <h4>{preview === currentImage ? 'Current Image' : 'New Image Preview'}</h4>
                        {preview !== currentImage && (
                          <button 
                            type="button" 
                            className="btn-remove-image"
                            onClick={handleRemoveImage}
                          >
                            Remove New Image
                          </button>
                        )}
                      </div>
                      <div className="image-preview">
                        <img src={preview} alt="Preview" />
                        <div className="preview-overlay">
                          <span>{preview === currentImage ? 'Current' : 'New'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="film-info-card">
                <h4>Film Information</h4>
                <div className="info-item">
                  <span className="info-label">Film ID:</span>
                  <span className="info-value">{id}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Current Image:</span>
                  <span className="info-value">
                    {currentImage ? 'Uploaded' : 'Not set'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {message.text && (
            <div className={`alert alert-${message.type}`}>
              {message.text}
            </div>
          )}
          
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => navigate('/admin/films')}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Updating...
                </>
              ) : 'Update Film'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default FilmEdit;